/**
 * TilePicker.tsx — WEB.4.UI.7
 *
 * Overlay HUD on top of HexBoard. NOT a modal — no scrim, no block.
 * Banner (top 64px, z:200) + strip (bottom 56px, z:200) sit above the board.
 * The existing HexBoard is the selection surface (passed new picker props).
 *
 * VISIBLE WHEN: tilePickerOpen === true (Zustand store)
 *
 * TEST PATH:
 *   1. Teleport — roll until landing on Tile 22 (Teleport type)
 *   2. Smog_Emergency — play card → TargetPicker → choose opponent → chains here
 *   3. Mercenaries — same as Smog_Emergency
 *   4. Enhanced_Measures / Infrastructure_Bailout — deferred (see TODO below)
 *   5. Disaster_Relief — deferred if damagedFlags not tracked
 *
 * Keyboard:
 *   Esc → closeTilePicker (card stays in hand for chained intents)
 *   Enter → confirmTilePick (if selectedPickerTile != null)
 *   Tab → cycle through valid tiles in ascending tile-index order
 *   1-9 → quick-select validTiles[N-1]
 *
 * Adder 1: Risk-preview — strip hint line shows selected tile info.
 * Adder 2: Auto-pan attention glow — first valid tile gets auto-pan-target class.
 * Edu fact ribbon: SKIPPED — see mockup designer reasoning.
 */

import type React from "react";
import { useEffect } from "react";
import type { I18n } from "../../i18n/useI18n.js";
import { useI18n } from "../../i18n/useI18n.js";
import { getCardById, useGameStore } from "../../store/game-store.js";
import styles from "./TilePicker.module.css";
import { resolveValidTiles } from "./filter.js";

// ---------------------------------------------------------------------------
// Card type → banner thumb class
// ---------------------------------------------------------------------------

function thumbClass(cardType: string | undefined, mode: string): string {
  if (mode === "teleport") return styles.bannerThumbTeleport ?? "";
  if (cardType === "Event" || cardType === "Crisis") return styles.bannerThumbEvent ?? "";
  if (cardType === "Policy") return styles.bannerThumbPolicy ?? "";
  if (cardType === "Mitigate") return styles.bannerThumbMitigate ?? "";
  return styles.bannerThumbMitigate ?? "";
}

function thumbGlyph(mode: string): string {
  switch (mode) {
    case "teleport":
      return "⊕";
    case "destroy":
      return "⚔";
    case "protect":
      return "🛡";
    case "heal":
      return "⚕";
    default:
      return "⚠";
  }
}

// ---------------------------------------------------------------------------
// Filter chip label helper
// ---------------------------------------------------------------------------

function filterChipText(mode: string, validCount: number, t: I18n["t"]): string {
  if (validCount === 0) return t("tilePicker.noValidTargets");
  switch (mode) {
    case "teleport":
      return t("tilePicker.teleportFilter");
    case "destroy":
      return t("tilePicker.destroyFilter", { count: validCount });
    case "protect":
      return t("tilePicker.protectFilter", { count: validCount });
    case "heal":
      return t("tilePicker.healFilter", { count: validCount });
    default:
      return t("tilePicker.facilitiesFilter", { count: validCount });
  }
}

function filterChipStyle(mode: string, validCount: number): string {
  if (validCount === 0) return styles.filterChipNone ?? "";
  if (mode === "destroy") return styles.filterChipDestroy ?? "";
  if (mode === "protect" || mode === "heal") return styles.filterChipSelf ?? "";
  return styles.filterChip ?? "";
}

// ---------------------------------------------------------------------------
// Verb text helper
// ---------------------------------------------------------------------------

function verbText(mode: string, validCount: number, t: I18n["t"]): string {
  if (validCount === 0) {
    return mode === "heal" ? t("tilePicker.noValidHeal") : t("tilePicker.noValidTargets");
  }
  switch (mode) {
    case "teleport":
      return t("tilePicker.pickDestination");
    case "destroy":
      return t("tilePicker.pickDestroy");
    case "protect":
      return t("tilePicker.pickProtect");
    case "heal":
      return t("tilePicker.pickHeal");
    default:
      return t("tilePicker.pickFacility");
  }
}

// ---------------------------------------------------------------------------
// Strip hint — selected tile info
// ---------------------------------------------------------------------------

function stripHintContent(
  selectedTile: number | null,
  validCount: number,
  mode: string,
  t: I18n["t"],
): React.ReactElement {
  if (validCount === 0) {
    return (
      <>
        <span className={styles.stripHintRed}>{t("tilePicker.noTargetHint")}</span>
      </>
    );
  }
  if (selectedTile === null) {
    return (
      <>
        <span className={styles.stripHintNone}>{t("tilePicker.noSelectionHint")}</span>
        <span>
          <span className={styles.kbd}>Esc</span> cancel · <span className={styles.kbd}>Tab</span>{" "}
          cycle · <span className={styles.kbd}>1-9</span> quick ·{" "}
          <span className={styles.kbd}>Enter</span> confirm
        </span>
      </>
    );
  }
  return (
    <>
      <span className={styles.stripHintSelected}>
        Selected: Tile{" "}
        <span className={styles.stripHintAmber}>{String(selectedTile).padStart(2, "0")}</span>
        {mode === "destroy" ? (
          <>
            {" "}
            · <span className={styles.stripHintRed}>Will DESTROY facility (no refund)</span>
          </>
        ) : mode === "protect" ? (
          <>
            {" "}
            · <span className={styles.stripHintGreen}>+ Immunity (1 use)</span>
          </>
        ) : mode === "teleport" ? (
          <> · Teleport here</>
        ) : (
          <> · Transfer facility at half cost</>
        )}
      </span>
    </>
  );
}

// ---------------------------------------------------------------------------
// TilePicker — public component
// ---------------------------------------------------------------------------

export function TilePicker(): React.ReactElement | null {
  const { t } = useI18n();
  const tilePickerOpen = useGameStore((s) => s.tilePickerOpen);
  const tilePickerFilter = useGameStore((s) => s.tilePickerFilter);
  const tilePickerMode = useGameStore((s) => s.tilePickerMode);
  const tilePickerCardId = useGameStore((s) => s.tilePickerCardId);
  const selectedPickerTile = useGameStore((s) => s.selectedPickerTile);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const builtFacilities = useGameStore((s) => s.builtFacilities);
  const ownedBy = useGameStore((s) => s.ownedBy);

  const closeTilePicker = useGameStore((s) => s.closeTilePicker);
  const selectPickerTile = useGameStore((s) => s.selectPickerTile);
  const confirmTilePick = useGameStore((s) => s.confirmTilePick);

  // Resolve valid tiles from filter
  const validTiles: number[] =
    tilePickerFilter !== null
      ? resolveValidTiles(
          tilePickerFilter,
          currentSeat,
          builtFacilities as Record<number, string | undefined>,
          ownedBy,
        )
      : [];

  const validSet = new Set(validTiles);
  const noValidTargets = validTiles.length === 0;
  const isConfirmEnabled =
    selectedPickerTile !== null && !noValidTargets && validSet.has(selectedPickerTile);

  // Keyboard handler
  useEffect(() => {
    if (!tilePickerOpen) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeTilePicker();
        return;
      }
      if (e.key === "Enter") {
        const s = useGameStore.getState();
        // Re-resolve valid tiles from current store state to avoid stale closure
        if (s.selectedPickerTile !== null && s.tilePickerFilter !== null) {
          const currentValid = new Set(
            resolveValidTiles(
              s.tilePickerFilter,
              s.currentSeat,
              s.builtFacilities as Record<number, string | undefined>,
              s.ownedBy,
            ),
          );
          if (currentValid.has(s.selectedPickerTile)) {
            confirmTilePick();
          }
        }
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        if (validTiles.length === 0) return;
        const currentIdx =
          selectedPickerTile !== null ? validTiles.indexOf(selectedPickerTile) : -1;
        const nextIdx = (currentIdx + 1) % validTiles.length;
        selectPickerTile(validTiles[nextIdx] ?? null);
        return;
      }
      // 1-9 quick-select
      const numKey = Number.parseInt(e.key, 10);
      if (numKey >= 1 && numKey <= 9) {
        const target = validTiles[numKey - 1];
        if (target !== undefined) selectPickerTile(target);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    tilePickerOpen,
    validTiles,
    selectedPickerTile,
    closeTilePicker,
    confirmTilePick,
    selectPickerTile,
  ]);

  if (!tilePickerOpen) return null;

  const cardDef = tilePickerCardId !== null ? getCardById(tilePickerCardId) : undefined;
  const cardType = cardDef?.cardType;
  const cardName = cardDef?.cardName ?? (tilePickerMode === "teleport" ? "Teleport" : "Action");

  const isDestroyMode = tilePickerMode === "destroy";
  const confirmBtnClass = [
    styles.confirmBtn,
    !isConfirmEnabled ? styles.confirmBtnDisabled : "",
    isDestroyMode && isConfirmEnabled ? styles.confirmBtnDestroy : "",
  ]
    .filter(Boolean)
    .join(" ");

  const confirmLabel = isDestroyMode ? "Confirm Destroy →" : "Confirm →";

  return (
    <div className={styles.wrapper} data-testid="tile-picker-wrapper">
      {/* Top banner */}
      <div className={styles.banner} data-testid="tile-picker-banner">
        <div
          className={`${styles.bannerThumb} ${thumbClass(cardType, tilePickerMode)}`}
          data-testid="tile-picker-thumb"
        >
          {thumbGlyph(tilePickerMode)}
        </div>

        <div className={styles.bannerText}>
          <div className={styles.bannerTitle} data-testid="tile-picker-card-name">
            {cardName.toUpperCase()}
          </div>
          <div className={styles.bannerVerb} data-testid="tile-picker-verb">
            {verbText(tilePickerMode, validTiles.length, t)}
          </div>
        </div>

        <div
          className={`${styles.filterChip} ${filterChipStyle(tilePickerMode, validTiles.length)}`}
          data-testid="tile-picker-filter-chip"
        >
          {filterChipText(tilePickerMode, validTiles.length, t)}
        </div>

        <button
          type="button"
          className={`${styles.cancelBtn}${noValidTargets ? ` ${styles.cancelBtnHighlight}` : ""}`}
          onClick={closeTilePicker}
          data-testid="tile-picker-cancel"
        >
          Cancel
        </button>
      </div>

      {/* Bottom strip */}
      <div className={styles.strip} data-testid="tile-picker-strip">
        <div className={styles.stripHint} data-testid="tile-picker-hint">
          {stripHintContent(selectedPickerTile, validTiles.length, tilePickerMode, t)}
        </div>

        <button
          type="button"
          className={confirmBtnClass}
          disabled={!isConfirmEnabled}
          onClick={isConfirmEnabled ? confirmTilePick : undefined}
          data-testid="tile-picker-confirm"
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Export the valid-tile resolver so App.tsx can pass highlightedTiles to HexBoard
// ---------------------------------------------------------------------------

export { resolveValidTiles };
